import { useCallback, useEffect, useState } from 'react';

import { Contact } from '@/core/notifications/model';
import {
  disablePushSubscription,
  enablePushSubscription,
  getCurrentDeviceEndpoint,
  getPushSubscriptionStatus,
  PushStatus,
  renameThisDevice,
} from '@/core/notifications/services/push-subscription-service';

export default function usePushSubscription(contacts: Contact[]) {
  const [status, setStatus] = useState<PushStatus>('checking');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentDeviceEndpoint, setCurrentDeviceEndpoint] = useState<string | null>(null);

  const refresh = useCallback(() => {
    return Promise.all([getPushSubscriptionStatus(contacts), getCurrentDeviceEndpoint()])
      .then(([nextStatus, endpoint]) => {
        setStatus(nextStatus);
        setCurrentDeviceEndpoint(endpoint);
      })
      .catch(() => setStatus('error'));
  }, [contacts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const enable = useCallback(async (deviceLabel?: string) => {
    setBusy(true);
    setErrorMessage(null);
    try {
      const next = await enablePushSubscription(deviceLabel);
      if (next === 'error') {
        setStatus(next);
        setErrorMessage('Could not enable push notifications on this device.');
      }
      // No explicit refresh() on success: the caller updates `contacts` via
      // onContactsChanged() after this resolves, which changes `refresh`'s
      // identity and re-fires it through the effect above. Refreshing here
      // instead would run against the stale `contacts` closure and make
      // getPushSubscriptionStatus think the subscription it just created is
      // orphaned, unsubscribing it immediately.
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      const next = await disablePushSubscription(contacts);
      if (next === 'error') {
        setStatus(next);
        setErrorMessage('Could not disable push notifications on this device.');
      } else {
        await refresh();
      }
    } finally {
      setBusy(false);
    }
  }, [refresh, contacts]);

  const rename = useCallback(
    async (label: string) => {
      setBusy(true);
      setErrorMessage(null);
      try {
        const next = await renameThisDevice(label, contacts);
        setStatus(next);
        if (next === 'error') setErrorMessage('Could not rename this device.');
        return next === 'on';
      } finally {
        setBusy(false);
      }
    },
    [contacts],
  );

  return { status, errorMessage, busy, currentDeviceEndpoint, enable, disable, rename, refresh };
}
